import {
  EXTERNAL_ACTIONS,
  FORCE_TO,
  INSTALLER_TYPES,
} from '../sunstone_credit/frontend/src/constants';
import { WithNonNullableKeys } from './utility';

// enums

export enum AccountAccountSource {
  ADVERTISEMENT = 'ADVERTISEMENT',
  CUSTOMER_EVENT = 'CUSTOMER_EVENT',
  EMPLOYEE_REFERRAL = 'EMPLOYEE_REFERRAL',
  EXTERNAL_REFERRAL = 'EXTERNAL_REFERRAL',
  GOOGLE_ADWORDS = 'GOOGLE_ADWORDS',
  OTHER = 'OTHER',
  PARTNER = 'PARTNER',
  PURCHASED_LIST = 'PURCHASED_LIST',
  TRADE_SHOW = 'TRADE_SHOW',
  WEBINAR = 'WEBINAR',
  WEBSITE = 'WEBSITE',
}

export enum AccountBusinessStructure {
  LLC = 'LLC',
  S_CORP = 'S-Corp',
  C_CORP = 'C-Corp',
  GENERAL_PARTNERSHIP = 'General Partnership',
  SOLE_PROPRIETORSHIP = 'Sole Proprietorship',
  NON_PROFIT = 'Non-Profit',
  HOA = 'HOA',
}

enum AccountLaunchStatus {
  APPROVED = 'Approved',
  SIGNED = 'Signed',
  LAUNCHED = 'Launched',
}

enum AccountPricingProfile {
  NORMAL = 'Normal',
  CED = 'CED',
  CUSTOM = 'Custom',
}

export enum AccountType {
  BORROWER = 'Borrower',
  CONSULTANT = 'Consultant',
  INSTALLER = 'Installer',
}

// SF Account.Contractor_Type__c picklist. Multi-select, semicolon-separated.
export enum ContractorType {
  SOLAR = 'Solar',
  FLEX = 'Flex',
  STORAGE = 'Storage',
  EV_CHARGERS = 'EV Chargers',
}

export enum AdvancedFundingApprovalStatus {
  APPROVED = 'Approved',
  APPLICATION_IN_PROGRESS = 'Application In-Progress',
  DECLINED = 'Declined',
  PRE_QUALIFIED = 'Pre-Qualified',
  ELIGIBLE = 'Eligible',
  NOT_ELIGIBLE = 'Not Eligible',
}

export enum DocumentApprovalSource {
  DOCUSIGN = 'DOCUSIGN',
  UPLOAD = 'UPLOAD',
}

export enum DocumentApprovalStatus {
  VERIFIED = 'VERIFIED',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  ISSUES_IDENTIFIED = 'ISSUES_IDENTIFIED',
  MISSING = 'MISSING',
  AWAITING_UPLOAD = 'AWAITING_UPLOAD',
  SENT_TO_BANK = 'SENT_TO_BANK',
}

export const DocumentsNeededStatuses = [
  DocumentApprovalStatus.MISSING,
  DocumentApprovalStatus.ISSUES_IDENTIFIED,
];

export enum DocumentUserGroup {
  INTERNAL = 'INTERNAL',
  INSTALLER = 'INSTALLER',
  CONSULTANT = 'CONSULTANT',
  BORROWER = 'BORROWER',
  INSTALLER_AND_CONSULTANT = 'INSTALLER_AND_CONSULTANT',
  CONSULTANT_AND_BORROWER = 'CONSULTANT_AND_BORROWER',
  BORROWER_AND_INSTALLER = 'BORROWER_AND_INSTALLER',
  ALL = 'ALL',
}

export const hasDocumentPermission = (
  accountType: AccountType,
  documentUserGroup: DocumentUserGroup
) => {
  if (accountType === AccountType.BORROWER) {
    return [
      DocumentUserGroup.ALL,
      DocumentUserGroup.BORROWER,
      DocumentUserGroup.BORROWER_AND_INSTALLER,
      DocumentUserGroup.CONSULTANT_AND_BORROWER,
    ].includes(documentUserGroup);
  } else if (accountType === AccountType.INSTALLER) {
    return [
      DocumentUserGroup.ALL,
      DocumentUserGroup.INSTALLER,
      DocumentUserGroup.INSTALLER_AND_CONSULTANT,
      DocumentUserGroup.BORROWER_AND_INSTALLER,
    ].includes(documentUserGroup);
  } else if (accountType === AccountType.CONSULTANT) {
    return [
      DocumentUserGroup.ALL,
      DocumentUserGroup.CONSULTANT,
      DocumentUserGroup.INSTALLER_AND_CONSULTANT,
      DocumentUserGroup.CONSULTANT_AND_BORROWER,
    ].includes(documentUserGroup);
  }
  return false;
};

export enum FileUploadSource {
  ROSEFIELD = 'ROSEFIELD',
  RAILWAY = 'RAILWAY',
}

// These are the possible types of entities that a file can be associated with (this is used to build the s3 url).
// The entity is the object type that the required document and file is associated with.
export enum DocumentForEntity {
  LOAN_APPLICATIONS = 'loan-applications',
  RELATED_PARTIES = 'related-parties',
  CHANGE_ORDERS = 'change-orders',
}

export enum ProductFamily {
  STANDARD = 'No Special Payment (no ITC)',
  SPECIAL_PAYMENT = 'Special Payment (ITC)',
  TAX_CREDIT = 'Tax Credit Transfer',
  PREMIUM = 'Premium',
  ELECTIVE_PAY = 'Elective Pay',
  FLEX = 'Flex',
}

export const RateSheetProducts = [
  ProductFamily.STANDARD,
  ProductFamily.SPECIAL_PAYMENT,
  ProductFamily.TAX_CREDIT,
  ProductFamily.FLEX,
];

export const ProductFamilyLoanTypeMapping = {
  [ProductFamily.STANDARD]: 'standard',
  [ProductFamily.SPECIAL_PAYMENT]: 'special',
  [ProductFamily.TAX_CREDIT]: 'tax_credit',
  [ProductFamily.PREMIUM]: 'premium',
  [ProductFamily.ELECTIVE_PAY]: 'elective_pay',
  [ProductFamily.FLEX]: 'flex',
};

export const LoanTypeMapping = {
  [ProductFamily.STANDARD]: 'Standard',
  [ProductFamily.SPECIAL_PAYMENT]: 'Special Payment',
  [ProductFamily.TAX_CREDIT]: 'Tax Credit Transfer',
  [ProductFamily.PREMIUM]: 'Premium',
  [ProductFamily.ELECTIVE_PAY]: 'Elective Pay',
  [ProductFamily.FLEX]: 'Flex',
};

export const LoanTypeBackwardsMapping = {
  Standard: {
    fullStr: ProductFamily.STANDARD,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.STANDARD],
  },
  'Special Payment': {
    fullStr: ProductFamily.SPECIAL_PAYMENT,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.SPECIAL_PAYMENT],
  },
  'Tax Credit Transfer': {
    fullStr: ProductFamily.TAX_CREDIT,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.TAX_CREDIT],
  },
  Premium: {
    fullStr: ProductFamily.PREMIUM,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.PREMIUM],
  },
  'Elective Pay': {
    fullStr: ProductFamily.ELECTIVE_PAY,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.ELECTIVE_PAY],
  },
  Flex: {
    fullStr: ProductFamily.FLEX,
    requestStr: ProductFamilyLoanTypeMapping[ProductFamily.FLEX],
  },
};

export const SpecialPaymentProducts = [
  ProductFamily.SPECIAL_PAYMENT,
  ProductFamily.TAX_CREDIT,
];

export enum LoanCategory {
  EXPRESS_OPCO = 'express_opco',
  EXPRESS_PROPCO = 'express_propco',
  EXPRESS_PLUS_OPCO = 'express_plus_opco',
  EXPRESS_PLUS_PROPCO = 'express_plus_propco',
  CORE_OPCO = 'core_opco',
  CORE_PROPCO = 'core_propco',
  NPO_FBO = 'npo_fbo',
  NPO_HOA = 'npo_hoa',
  NPO_EDUCATIONAL_INSTITUTE = 'npo_educational_institute',
  NPO_OTHER = 'npo_other',
  INDUSTRY_PG_OPCO = 'industry_pg_opco',
  INDUSTRY_PG_PROPCO = 'industry_pg_propco',
  PPA_FINANCE = 'ppa_finance',
  EXPRESS = 'Express',
  EXPRESS_PLUS = 'Express+',
  CORE = 'Core',
}

// types

export type ApplicationExternalAction =
  | 'Application Not Started'
  | 'Application Not Complete'
  | 'Waiting on Borrower'
  | 'Loan Package Sent'
  | 'UW In Progress'
  | 'Approved With Conditions'
  | 'Provide Missing Information'
  | 'Loan Processing'
  | 'Loan Approved'
  | 'Loan Declined'
  | 'Canceled';

export type ChangeOrderDocumentTypes = keyof Pick<
  RequiredDocuments,
  'Change Order Contract' | 'Solar Loan Proposal' | 'Borrower Change Awareness'
>;

export type Document = {
  name: string;
  url: string;
  last_modified_date: string;
  id: SFId;
};

export type ExternalStatusAction =
  | 'Application Created'
  | 'Application In Progress'
  | 'Application Submitted'
  | 'Loan Package'
  | 'Complete';

export type FeeByProductFamily = {
  [key in RateSheetFee['product_family']]?: RateSheetFee[];
};

export type FundedProjectMessage = {
  type: 'info' | 'warning' | 'error';
  message: string;
};

export type InstallerDashboardSummary = {
  total_applications: number;
  total_applicants: number;
  completed_applications: number;
  active_applications: number;
};

export type InstallerProjectSummary = {
  total_paid_to_date: number;
  net_fundings: number;
  projects_in_progress: number;
  completed_projects: number;
};

export type NonUniqueRequiredDocuments =
  | 'Proof of Offtaker Creditworthiness'
  | 'Offtaker Utility Bill'
  | 'Offtaker PPA or Lease Agreement'
  | 'Community Solar Financial Projections'
  | 'Utility Bills'
  | 'Additional Document'
  | 'Incentive Documents';

export type InstallerProgramType =
  | 'Milestone Program'
  | 'Greentech Direct Pay Program'
  | 'Milestone Plus'
  | 'Milestone Full Approval'
  | 'Milestone Managed Approval'
  | 'Milestone Deal Approval'
  | 'Flex Milestone Plus';

// A tuple of NonUniqueRequiredDocuments type where each value is only encountered once
export const NonUniqueRequiredDocumentsList: NonUniqueRequiredDocuments[] = [
  'Community Solar Financial Projections',
  'Offtaker PPA or Lease Agreement',
  'Offtaker Utility Bill',
  'Proof of Offtaker Creditworthiness',
  'Utility Bills',
  'Additional Document',
  'Incentive Documents',
];

export enum PlaidAssetReportStatus {
  'SUCCESS' = 'SUCCESS',
  'FAILED' = 'FAILED',
  'PENDING' = 'PENDING',
  'DOES_NOT_EXIST' = 'DOES_NOT_EXIST',
  'READY' = 'READY',
  'PROCESSING' = 'PROCESSING',
}

export type PlaidAssetReport = {
  asset_report_status: PlaidAssetReportStatus;
  institution: null | string;
  masks: null | string[];
};

export type RateSheetFee = WithNonNullableKeys<
  Fee,
  | 'dealer_fee'
  | 'interest_rate'
  | 'pricing_grouping'
  | 'product_family'
  | 'loan_term_years'
  | 'amortization_years'
>;

export type SunstoneDocuments = {
  plaid_asset_report?: PlaidAssetReport;
  TR: SunstoneDocument;
  TR1: SunstoneDocument;
  TR2: SunstoneDocument;
  'Tax Returns 2 Years Ago': SunstoneDocument;
  'Tax Returns 3 Years Ago': SunstoneDocument;
  'Tax Returns Last Year': SunstoneDocument;
  FIN?: SunstoneDocument;
  'Interim Financial Statement': SunstoneDocument;
  BIZ?: SunstoneDocument;
  'Business Organization': SunstoneDocument;
  BS: SunstoneDocument;
  BS1: SunstoneDocument;
  BS2: SunstoneDocument;
  'Bank Statements 2 Months Ago': SunstoneDocument;
  'Bank Statements 3 Months Ago': SunstoneDocument;
  'Bank Statements Last Month': SunstoneDocument;
  OWN: SunstoneDocument;
  'Proof of Ownership of the System Property': SunstoneDocument;
  PGTR1: SunstoneDocument;
  PGEX1: SunstoneDocument;
  PGTR2: SunstoneDocument;
  PGEX2: SunstoneDocument;
  PGTR3: SunstoneDocument;
  PGEX3: SunstoneDocument;
  'PG Tax Returns Last Year': SunstoneDocument;
  'PG Tax Extension Last Year': SunstoneDocument;
  'PG Tax Returns 2 Years Ago': SunstoneDocument;
  'PG Tax Extension 2 Years Ago': SunstoneDocument;
  'PG Tax Returns 3 Years Ago': SunstoneDocument;
  'PG Tax Extension 3 Years Ago': SunstoneDocument;
  PRINCIPAL_ID?: SunstoneDocument;
  'Principal ID'?: SunstoneDocument;
  'Articles of Organization/Incorporation'?: SunstoneDocument;
  'Non-Profit Board Resolution'?: SunstoneDocument;
  'Non-Profit Bylaws'?: SunstoneDocument;
  'Property Company Rent Roll'?: SunstoneDocument;
  'Lease of the property where the system will be installed'?: SunstoneDocument;
  'Lease of the System Property'?: SunstoneDocument;
  'Proof of Ownership of the property where the system will be installed'?: SunstoneDocument;
  'Offtaker PPA or Lease Agreement'?: SunstoneDocument[];
  'Proof of Offtaker Creditworthiness'?: SunstoneDocument[];
  'Offtaker Lease or Proof of Ownership'?: SunstoneDocument;
  'Offtaker Utility Bill'?: SunstoneDocument[];
  'PPA/Lease Financial Projections'?: SunstoneDocument;
  'Subscriber Management Company Contract'?: SunstoneDocument;
  'Community Solar Financial Projections'?: SunstoneDocument[];
  'Approval for Community Solar Delivery'?: SunstoneDocument;
  'Financial Statements YTD'?: SunstoneDocument;
  'Financial Statements Last Year'?: SunstoneDocument;
  'Financial Statements One Year Ago'?: SunstoneDocument;
  'Financial Statements Two Years Ago'?: SunstoneDocument;
  'IRS Form SS-4'?: SunstoneDocument;
  'Tax Returns Extension (Form 7004)'?: SunstoneDocument;
  'Debt Schedule'?: SunstoneDocument;
  // Change order documents
  'Change Order Contract'?: SunstoneDocument;
  // Religious Organization documents
  'Religious Organization Attendance'?: SunstoneDocument;
  'Religious Organization Donations'?: SunstoneDocument;
  // HOA Documents
  'Member Assessment Policy'?: SunstoneDocument;
  'Operating and Reserve Budgets'?: SunstoneDocument;
  'Current Occupant Ledger'?: SunstoneDocument;
  'Meeting Minutes'?: SunstoneDocument;
  'HOA Bylaws'?: SunstoneDocument;
  'Property Company Mortgage Statement'?: SunstoneDocument;
  'Mortgage Statement'?: SunstoneDocument;
  'Additional Document'?: SunstoneDocument[];
  FinancialStatementQ1: SunstoneDocument;
  FinancialStatementQ2: SunstoneDocument;
  FinancialStatementQ3: SunstoneDocument;
  FinancialStatementLastYearQ3: SunstoneDocument;
  FinancialStatementFullYear: SunstoneDocument;
  'Financial Statement Q1': SunstoneDocument;
  'Financial Statement Q2': SunstoneDocument;
  'Financial Statement Q3': SunstoneDocument;
  'Financial Statement Last Year Q3': SunstoneDocument;
  'Financial Statement Full Year': SunstoneDocument;
  'Financial Statement Two Years Ago Full Year': SunstoneDocument;
  'Financial Statement Three Years Ago Full Year': SunstoneDocument;
  'Financial Statement Four Years Ago Full Year': SunstoneDocument;

  // New Document Types
  'Business Secretary of State'?: SunstoneDocument;
  'Equipment Order Draw Form Signed'?: SunstoneDocument;
  'Equipment Order Draw Form'?: SunstoneDocument;
  'GA Equipment Order Form'?: SunstoneDocument;
  'GA Loan Agreement'?: SunstoneDocument;
  'GA Mechanical Completion Form'?: SunstoneDocument;
  'GA Racking Completion Form'?: SunstoneDocument;
  'HOA Current Occupant Ledger'?: SunstoneDocument;
  'Loan Agreement Signed'?: SunstoneDocument;
  'Mechanical Completion Draw Form (signed)'?: SunstoneDocument;
  'Mechanical Completion Draw Form'?: SunstoneDocument;
  'Racking Completion Draw Form (unsigned)'?: SunstoneDocument;
  'Racking Completion Draw Form'?: SunstoneDocument;
  'UCC-1 Financing Statement'?: SunstoneDocument;
  'UCC-3'?: SunstoneDocument;
  'UCC-1 Fixture Notice'?: SunstoneDocument;
  'PV Watts'?: SunstoneDocument;
  'Borrower Profile'?: SunstoneDocument;
  'Property Appraisal'?: SunstoneDocument;
};

export type SFAutoFields = keyof SFObject;

export type SFDate = string;

export type SFId = string;

export type USState =
  | 'Alabama'
  | 'Alaska'
  | 'Arizona'
  | 'Arkansas'
  | 'California'
  | 'Colorado'
  | 'Connecticut'
  | 'Delaware'
  | 'Florida'
  | 'Georgia'
  | 'Hawaii'
  | 'Idaho'
  | 'Illinois'
  | 'Indiana'
  | 'Iowa'
  | 'Kansas'
  | 'Kentucky'
  | 'Louisiana'
  | 'Maine'
  | 'Maryland'
  | 'Massachusetts'
  | 'Michigan'
  | 'Minnesota'
  | 'Mississippi'
  | 'Missouri'
  | 'Montana'
  | 'Nebraska'
  | 'Nevada'
  | 'New Hampshire'
  | 'New Jersey'
  | 'New Mexico'
  | 'New York'
  | 'North Carolina'
  | 'North Dakota'
  | 'Ohio'
  | 'Oklahoma'
  | 'Oregon'
  | 'Pennsylvania'
  | 'Rhode Island'
  | 'South Carolina'
  | 'South Dakota'
  | 'Tennessee'
  | 'Texas'
  | 'Utah'
  | 'Vermont'
  | 'Virginia'
  | 'Washington'
  | 'Washington, D.C.'
  | 'West Virginia'
  | 'Wisconsin'
  | 'Wyoming';

export type UtilityAPIConnectionStatus =
  | 'PENDING_AUTHORIZATION'
  | 'UPDATING'
  | 'BILLS_COLLECTED'
  | 'ERROR';

export type UtilityAPIConnection = {
  application_id: string;
  auth_form_url: string;
  status: UtilityAPIConnectionStatus;
  bills: Record<string, any> | null;
  error_message?: string | null;
  referral?: string;
  authorization_uid?: string;
  meter_uid?: string;
};

export type YesNo = 'Yes' | 'No';

export type YesNoUppercase = 'YES' | 'NO';

// interfaces

export type APIError = {
  /**Error description coming from the API */
  detail: string;
  /**If incoming error is a validation error, this will be a list or a dictionary of normalized errors. */
  errors?: Record<string, any>;
};

export interface Account {
  id: string;
  account_domain_url: string;
  account_number: number;
  account_source: AccountAccountSource;
  anticipated_annual_loan_volume?: number;
  borrower_contact?: Contact;
  borrower_contact_email: string;
  borrower_contact_name: string;
  business_name: string;
  business_phone: string;
  business_structure: AccountBusinessStructure;
  contractor_type?: string;
  created_date: Date;
  custom_pricing_current_rates_pdf?: string;
  custom_pricing_future_rates_pdf?: string;
  dba_if_applicable?: string;
  description: string;
  ein: number;
  encrypted_data: string;
  installer: string;
  installer_account: number;
  installer_advance_funding?: AdvancedFundingApprovalStatus;
  installer_advance_funding_limit?: number;
  installer_bank_account_name?: string;
  installer_bank_account_number?: string;
  installer_bank_name?: string;
  installer_bank_routing_number?: string;
  installer_business_name: string;
  installer_contact?: Contact;
  installer_contact_email: string;
  installer_contact_first_name: string;
  installer_contact_last_name?: string;
  installer_contact_name: string;
  installer_contact_phone?: string;
  installer_contact_position_title?: string;
  installer_current_rates_calculator?: string;
  installer_current_rates_calculator_pdf?: string;
  installer_dba_if_applicable: string;
  installer_ein: number;
  installer_future_rates_calculator?: string;
  installer_future_rates_calculator_pdf?: string;
  installer_liaison_email: string;
  installer_liaison_first_name: string;
  installer_liaison_last_name: string;
  installer_liaison_phone: string;
  installer_liaison_position_title: string;
  installer_license_number: number;
  installer_phone: string;
  installer_programs: InstallerProgram[];
  installer_rates_change_date?: string;
  installer_tin: number;
  installer_website: string;
  jigsaw: string;
  jigsaw_company_id: string;
  last_activity_date: Date;
  last_modified_date: Date;
  last_referenced_date: Date;
  last_viewed_date: Date;
  launch_status?: AccountLaunchStatus;
  liaison_position_title: string;
  license_number: number;
  license_type: string;
  llc_s_corp_c_corp_general_partnership: number;
  location_of_bank?: string;
  master_record: number;
  milestone_program?: string;
  name_of_bank: string;
  net_income_annual: number;
  number_of_employees: number;
  owner: User;
  phone: string;
  pricing_calculator_current?: string;
  pricing_calculator_future?: string;
  greentech_direct_pay?: string;
  pricing_profile?: AccountPricingProfile;
  record_type: number;
  routing_number: number;
  sic_desc: string;
  system_modstamp: Date;
  type: AccountType;
  tin: number;
  website: string;
  test_account?: boolean;
}

export enum AssetType {
  SOLAR = 'Solar',
  ENERGY_STORAGE_BESS = 'Energy Storage/BESS',
  EV_CHARGERS = 'EV Chargers',
  ROOFING = 'Roofing',
  HVAC = 'HVAC',
  LED_LIGHTING = 'LED Lighting',
  OTHER = 'Other',
}

export interface Asset {
  id?: string;
  name?: string;
  type?: AssetType | string;
  price?: number;
  loan_application?: string;
}

export enum ProjectType {
  SOLAR = 'Solar',
  SOLAR_PLUS = 'Solar+',
  FLEX = 'Flex',
}

export type BypassPGValue = 'Yes' | 'No' | null;

export interface Application {
  id?: any;
  installer_program: InstallerProgram;
  loan_status_tasks?: any;
  principals?: RelatedParty[];
  is_personal_guarantor: 'Yes' | 'No' | null;
  outstanding_loans?: MaterialLoanObligation[];
  name?: string;
  created_date: string;
  app_submission_deadline?: string;
  application_start_date?: string;
  last_modified_date?: string;
  loan_version?: LoanVersion;
  system_modstamp?: string;
  last_activity_date?: string | null;
  last_viewed_date?: string | null;
  last_referenced_date?: string | null;
  sys_prop_lease_remaining_months?: number | null;
  applicant_ip_address?: string | null;
  business_address_city?: string | null;
  crb_application_id?: string | null;
  borrower_dba?: string | null;
  borrower_tin?: number | null;
  borrower_structure?:
    | 'LLC'
    | 'LLP'
    | 'S-CORP'
    | 'C-CORP'
    | 'GENERAL PARTNERSHIP'
    | 'SOLE PROPRIETORSHIP'
    | 'NON-PROFIT'
    | 'HOA'
    | 'Irrevocable Trust'
    | 'Revocable Trust'
    | null;
  borrower_gross_revenue_latest?: number | null;
  borrower_net_income_annual_after_tax?: number | null;
  sys_prop_address?: string | null;
  sys_prop_address_line_2?: string | null;
  project_timeline_estimation?:
    | 'The project is already complete'
    | 'The project is currently under construction'
    | 'Construction has not started, expect completion within 3 months'
    | 'Construction has not started, expect completion within 6 months'
    | 'Construction has not started, expect completion in more than 6 months'
    | 'Construction has not started, construction timeline currently unknown'
    | null;
  system_mounting_hardware?: string | null;
  sys_prop_ownership_status_picklist?: 'LEASED' | 'OWNED' | null;
  sys_prop_lease_monthly_payment?: number | null;
  sys_prop_spe_name?: string | null;
  sys_prop_mortgage_has_spe?: 'YES' | 'NO' | null;
  sys_prop_monthly_mortgage_payment?: number | null;
  borrower_advance?:
    | 'Yes'
    | 'No'
    | 'Milestone Plus'
    | 'Milestone Full Approval'
    | 'Milestone Deal Approval'
    | 'Milestone Managed Approval'
    | null;
  borrower_signer_role?: string | null;
  business_address_state?: USState | null;
  borrower_industry_lines_of_business?: string | null;
  borrower_date_business_established_date?: string | null;
  borrower_does_owe_taxes_from_prev_years?: 'YES' | 'NO' | null;
  borrower_def_in_pending_claim_lawsuit?: 'YES' | 'NO' | null;
  borrower_judgment_in_past_5_years?: 'YES' | 'NO' | null;
  borrower_declared_bankruptcy_past_10_yrs?: 'YES' | 'NO' | null;
  borrower_has_potential_ownership_change?: 'Yes' | 'No' | null;
  borrower_has_credit_under_other_name?: 'Yes' | 'No' | null;
  borrower_is_high_risk?: 'Yes' | 'No' | null;
  business_address_zip_code?: string | null;
  mailing_address_city?: string | null;
  mailing_address_state?: USState | null;
  mailing_address_zip_code?: string | null;
  sys_prop_address_city?: string | null;
  sys_prop_address_state?: USState | null;
  rec_info?: string | null;
  sys_prop_address_zip_code?: string | null;
  business_address?: string | null;
  business_address_line_2?: string | null;
  mailing_address?: string | null;
  mailing_address_line_2?: string | null;
  sys_prop_lease_remaining_years?: number | null;
  sys_prop_landlord_consent_confirmation?: 'Yes' | 'No' | null;
  submission_date_stage1?: string | null;
  submission_date_stage2?: string | null;
  borrower_contact_full_name?: string;
  borrower_contact_email?: string;
  installer_business_name?: string | null;
  borrower_business_name?: string;
  loan_amount?: number | null;
  borrower_state_of_organization?: string | null;
  borrower_esign_name?: string | null;
  borrower_business_email?: string | null;
  borrower_business_phone_number?: string | null;
  business_ownership_explanation?: string | null;
  sys_prop_is_subleasing?: 'YES' | 'NO' | null;
  sys_prop_months_remaining_on_sublease?: number | null;
  booking_received_date?: string | null;
  booking_sent_date?: string | null;
  crb_compliance_passed?: string | null;
  crb_send_date?: string | null;
  date_funded?: string | null;
  date_ready_to_fund?: string | null;
  external_status_action: EXTERNAL_ACTIONS;
  external_status_borrower_description?: string | null;
  external_status_installer_description?: string | null;
  external_status_stage?:
    | 'Application Created'
    | 'Application In Progress'
    | 'Application Submitted'
    | 'Loan Package'
    | 'Complete'
    | 'Expired'
    | 'Closed'
    | null;
  installer_contact_email?: string | null;
  internal_status?:
    | 'Application Created'
    | 'Application Pending'
    | 'Application Pending Change Request'
    | 'Application Created Pending CR'
    | 'Documents Pending'
    | 'Pending Borrower'
    | 'Pending Installer'
    | 'Application Complete'
    | 'UW In-Progress'
    | 'UW Complete'
    | 'Compliance Pending'
    | 'Compliance Fail'
    | 'Compliance Pass'
    | 'Agreements Pending'
    | 'Agreements Executed'
    | 'Agreement Complete'
    | 'Ready to Fund'
    | 'Loan Funded'
    | 'Loan Approved'
    | 'Declined'
    | 'Canceled: Installer'
    | 'Canceled: Borrower'
    | 'Canceled: Sunstone'
    | 'Canceled: Do Not Contact'
    | 'Delayed'
    | 'Canceled: Expired'
    | 'Pending Change Request'
    | 'Closed'
    | 'Re-Underwriting in Progress'
    | 'Re-Underwriting Documents Received'
    | null;
  last_uw_communication_date?: string | null;
  loan_agreement_sent_date?: string | null;
  loan_agreement_signed_date?: string | null;
  decision_date?: string | null;
  ucc_received_date?: string | null;
  ucc_sent_date?: string | null;
  project_type?: ProjectType | null;
  has_storage_asset?: boolean;
  has_non_solar_component?: string | null;
  non_solar_components?: string | null;
  non_solar_amount?: number | null;
  other_non_solar_amount?: number | null;
  roof_repair_amount?: number | null;
  ev_charging_amount?: number | null;
  energy_storage_amount?: number | null;
  other_non_solar?: string | null;
  sys_prop_mortgage_lender_name?: string | null;
  installer_contact?: SFId | null;
  consultant_contact?: SFId | null;
  product: Product;
  borrower_contact?: any;
  installer?: Account;
  consultant_account?: Account | null;
  borrower_account?: SFId;
  deal_description_and_context?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email_address?: string | null;
  primary_contact_phone_number?: string | null;
  mailing_address_same_as_bussiness?: 'Yes' | 'No' | null;
  solar_system_power_usage?:
    | "Offset Borrower's utility bill"
    | 'Sold to another entity'
    | null;
  solar_system_power_company_type?:
    | 'Operating Company'
    | 'Property Company'
    | 'Agriculture'
    | 'Non-Profit'
    | 'HOA'
    | null;
  utility_bill_payer?: 'Tenant' | 'Landlord' | null;
  non_profit_received_board_approval?: 'Yes' | 'No' | null;
  non_profit_bylaws_reviewed_updated?: 'Yes' | 'No' | null;
  solar_system_power_describe_company?: string | null;
  system_power_single_or_multiple_users?:
    | 'Single User'
    | 'Multiple Users'
    | null;
  legal_name_of_offtaker?: string | null;
  is_partnering_with_tax_equity_investor?: 'Yes' | 'No' | null;
  tax_equity_investor_deal_funding_percent?: string | null;
  system_power_as_percent_of_offtaker_need?: string | null;
  offtaker_utility_rate_discount?: string | null;
  community_solar_or_ppa_lease?:
    | 'PPA/Lease Agreement'
    | 'Community Solar Arrangement'
    | null;
  legal_names_of_offtakers?: string | null;
  system_power_number_of_offtakers?: number | null;
  sys_prop_address_is_same_as_business?: 'Yes' | 'No' | null;
  borrower_is_currently_paying_mortgage?: 'Yes' | 'No' | null;
  system_property_legal_name_of_owner?: string | null;
  sys_prop_owner_affiliated_with_borrower?: 'Yes' | 'No' | null;
  system_property_lease_expiration_year?: number | null;
  system_property_appraised_value?: number | null;
  system_property_mortgage_opened_year?: number | null;
  system_property_initial_mortgage_amount?: number | null;
  system_property_mortgage_due_amount?: number | null;
  system_property_mortgage_maturity_year?: number | null;
  fin_info_name_credit_was_obtained_under?: string | null;
  fin_info_borrower_owes_taxes_explanation?: string | null;
  fin_info_borrower_pending_lawsuit_claim?: string | null;
  fin_info_awarded_judgement_explanation?: string | null;
  fin_info_borrower_bankruptcy_explanation?: string | null;
  system_power_rent_roll_or_tenant_info?:
    | 'Upload rent roll'
    | 'Enter information for a single tenant'
    | null;
  sys_power_property_company_tenant_name?: string | null;
  property_company_tenant_move_in_year?: number | null;
  property_company_tenant_lease_term?: number | null;
  property_company_tenant_renewal_year?: number | null;
  property_company_tenant_renewal_length?: number | null;
  prop_com_tenant_monthly_rent_payment?: number | null;
  prop_com_tenant_payments_satisfactory?: 'Yes' | 'No' | null;
  business_established_year?: number | null;
  management_years_of_industry_experience?: number | null;
  was_business_acquired?: 'Yes' | 'No' | null;
  years_management_in_place?: number | null;
  borrower_is_high_risk_explanation?: string | null;
  applicant_filed_tax_returns_in_the_past: 'Yes' | 'No' | null;
  applicant_filed_last_year_tax_returns: 'Yes' | 'No' | null;
  closed_reason:
    | 'Loan Concerns - Economic'
    | 'Loan Concerns - Terms and Process/Payment Terms'
    | 'Project Issues Construction'
    | 'Invalid Application'
    | 'Solar Loan Concerns'
    | 'Sunstone Terms/Requirements'
    | 'Solar Project Concerns'
    | 'Expired'
    | 'Not Supported'
    | 'Issue With Installer'
    | 'Loan Package Unsigned'
    | 'Solar Project Incomplete'
    | 'Borrower Internal Changes'
    | 'Project Issues'
    | null;
  close_requested_by: 'Borrower' | 'Installer' | 'Sunstone' | null;
  closed_sub_reason:
    | 'Installer went out of business'
    | 'Chose to Pay Cash'
    | 'Dealer Fee/Interest Rate'
    | 'Other'
    | 'Did Not Provide Documentation for Approval Condition'
    | 'Specific Loan Terms'
    | 'Borrower does not want to work with Sunstone'
    | 'Payment Terms'
    | 'Borrower did not or is concerned they would not receive incentives'
    | 'Project economics were not acceptable'
    | 'Concerns about the Installer'
    | 'Does not want solar'
    | 'Incorrect Loan Amount/Contact/Name/Product'
    | 'Multiple systems'
    | 'Other error'
    | 'Construction, permitting or interconnection issues'
    | 'Application Incomplete'
    | 'Application Not Started'
    | 'Borrower dissatisfied'
    | 'Sunstone Loan is too expensive'
    | 'Borrower does not want to provide a PG'
    | 'Borrower does not want to proceed with solar project'
    | 'Borrower does not want to take on a loan'
    | 'Borrower does not like Sunstone loan conditions'
    | 'Borrower is uncertain if they qualify for ITC or another incentive'
    | 'External factors'
    | 'Incorrect Amount'
    | 'Incorrect Borrower Contact'
    | 'Incorrect Business Name'
    | 'Incorrect Product'
    | 'Loan package not signed within 30 days of receipt'
    | 'Loan package not signed within Sunstone approved timeline'
    | 'Loan Product'
    | 'Multiple Systems'
    | 'Other Error'
    | 'Poor Sunstone application experience'
    | 'Project Type'
    | 'Rate Increase'
    | 'Solar project did not fund within 180 days of approval'
    | 'Solar project not funded within Sunstone approved timeline'
    | 'Test Application'
    | null;
  closed_reason_notes: string | null;
  web_user_can_close: 'Yes' | 'No' | null;
  shown_to_borrower: 'Yes' | 'No' | null;
  is_religious_organization: 'Yes' | 'No' | null;
  parent_religious_organization_support: 'Yes' | 'No' | null;
  years_religious_leader_position: number | null;
  change_orders: ChangeOrder[];
  utility_api_connection: UtilityAPIConnection | null;
  borrower_attests_no_debt: boolean;
  borrower_attests_no_debt_pick: 'Yes' | 'No' | null; // This is a new picklist field for the borrower attests no debt question (Ticket #3392)
  uw_requests: ChecklistItem[];
  system_size_in_k_ws: number | null;
  year_1_system_production_kwh: number | null;
  year_1_total_solar_savings: number | null;
  total_system_cost?: number | null;
  extensions_count: number;
  special_payment_percentage?: number;
  loan_category?: LoanCategory | null;
  required_documents?: any[];
  documents?: any[];
  business_type_for_profit_or_non_profit?: 'For-Profit' | 'Non-Profit' | null;
  for_profit_company_type?:
    | 'Sells goods or services'
    | 'Collects rent from tenants'
    | 'Sells solar-generated power to third-parties'
    | null;
  non_profit_organization_type?:
    | 'Faith-based organization'
    | "Homeowner's association"
    | 'Educational institution'
    | 'Other'
    | null;
  industry_borrower_reported?:
    | 'Agriculture'
    | 'Energy & Utilities'
    | 'Entertainment & Recreation'
    | 'Food & Beverage - Restaurants and Bars'
    | 'Food & Beverage - Other'
    | 'Gym & Fitness'
    | 'Healthcare'
    | 'Hospitality'
    | 'Manufacturing, Industrial, & Construction'
    | 'Mining, Oil, & Gas'
    | 'Non-Profit'
    | 'Professional Services'
    | 'Public Administration'
    | 'Real Estate'
    | 'Retail & Consumer Goods & Services'
    | 'Technology, Media, & Telecommunications'
    | 'Transportation & Warehousing'
    | 'Wholesale Goods'
    | 'Other';
  system_property_landlord_type?:
    | 'Borrower'
    | 'Another Individual'
    | 'Another Business'
    | null;
  system_property_landlord_ownership?: number | null;
  system_property_has_related_tenants?: 'Yes' | 'No' | null;
  system_property_related_tenant_name?: string | null;
  feature_flags?: string;
  business_ownership_status?: any[] | null;
  number_of_employees_range?: string | null;
  members_served?: number | null;
  is_own_property_under_construction?: 'Yes' | 'No' | null;
  willing_to_provide_commercial_guarantor?: 'Yes' | 'No' | null;
  is_property_tenant_willing_to_be_cg?: 'Yes' | 'No' | null;
  special_payment_acknowledgment?: boolean;
  milestone_payment_acknowledgment?: boolean;
  maturity_payment_acknowledgment?: boolean;
  commercial_guarantor_legal_name?: string | null;
  monetize_tax_credit_check?: 'Yes' | 'No' | null;
  force_to?: FORCE_TO | null;
  installer_changes_need_review?: boolean;
  is_flagged_non_profit_by_installer?: 'Yes' | 'No' | null;
  begin_installation_expected?:
    | 'As soon as possible'
    | 'Within the next 3-months'
    | 'Within the next 6-months'
    | 'Sometime in the next year'
    | 'I am still evaluating whether to move forward with the project'
    | null;
  primary_motivation_for_solar?:
    | 'Utility Bill Savings'
    | 'Energy Resilience'
    | 'Environmental'
    | 'Tax Credits or Other Incentives'
    | 'Other'
    | null;
  primary_motivation_for_solar_other?: string | null;
  upload_or_list_debt_obligations?:
    | 'Debt Schedule'
    | 'List out debt obligations'
    | null;
  motivation_additional_information?: string | null;
  interest_payer_during_construction?: 'Installer' | 'Borrower' | null;
  net_funding?: number | null;
  bypass_pg?: BypassPGValue;
  project_nickname?: string | null;
  borrower_project_nickname?: string | null;
}

// This is for the installer Dashbaord - minimal version of Application for dashboard
export interface DashboardApplicationAsset {
  id: string;
  name: string | null;
  type: string | null;
  price: number | null;
}

export interface DashboardApplication
  extends Pick<
    Application,
    | 'id'
    | 'app_submission_deadline'
    | 'borrower_advance'
    | 'borrower_contact_full_name'
    | 'borrower_business_name'
    | 'borrower_business_phone_number'
    | 'borrower_contact_email'
    | 'closed_reason'
    | 'closed_sub_reason'
    | 'consultant_account'
    | 'external_status_action'
    | 'external_status_installer_description'
    | 'external_status_stage'
    | 'installer'
    | 'installer_program'
    | 'name'
    | 'loan_amount'
    | 'rec_info'
    | 'web_user_can_close'
    | 'internal_status'
    | 'loan_version'
    | 'system_size_in_k_ws'
    | 'installer_business_name'
    | 'project_nickname'
    | 'project_timeline_estimation'
    | 'interest_payer_during_construction'
    | 'deal_description_and_context'
    | 'project_type'
    | 'special_payment_percentage'
    | 'business_type_for_profit_or_non_profit'
    | 'for_profit_company_type'
    | 'non_profit_organization_type'
    | 'industry_borrower_reported'
    | 'sys_prop_address_state'
    | 'is_flagged_non_profit_by_installer'
  > {
  borrower_contact_first_name?: string | null;
  borrower_contact_last_name?: string | null;
  borrower_contact_position_title?: string | null;
  borrower_account_id?: string | null;
  borrower_contact_id?: string | null;
  product: string | null;
  assets: DashboardApplicationAsset[];
  amortization_years: number;
  apr_percentage: number;
  change_orders: DashboardChangeOrder[];
  error_code?: string;
  loan_term_years: number;
  loan_status_tasks: Task[];
  system_address: string;
  can_create_change_request: boolean;
  required_documents: RequiredDocument[];
  loan_term: {
    net_funding: number | null;
  };
}

export interface ChangeOrder extends SFObject {
  loan_application: string | null;
  date_submitted: string | null;
  external_status:
    | 'Draft'
    | 'Under Review'
    | 'Approved'
    | 'Declined'
    | 'Canceled'
    | null;
  internal_status:
    | 'Draft'
    | 'Submitted'
    | 'Under Review'
    | 'Investor Review'
    | 'CRB First Pass'
    | 'Loan Agreement Sent'
    | 'Loan Agreement Executed'
    | 'Declined'
    | 'Canceled'
    | 'Completed'
    | null;
  decline_reason: string | null;
  new_loan_amount: number | null;
  project_has_a_solar_component: 'Yes' | 'No' | null;
  cost_of_the_solar_component_changing: 'Yes' | 'No' | null;
  description_of_solar_changes: string | null;
  additional_solar_equipment_installation: 'Yes' | 'No' | null;
  equipment_new_or_used: 'New' | 'Used' | null;
  name_of_subcontractor: string | null;
  subcontractor_warranty: string | null;
  equipment_make_and_model: string | null;
  who_is_completing_work: 'Us' | 'Subcontractor' | null;
  subcontractor_address: string | null;
  subcontractor_address_line_2: string | null;
  subcontractor_city: string | null;
  subcontractor_zipcode: string | null;
  subcontractor_state: string | null;
  new_system_size_k_w: number | null;
  new_first_year_production_k_wh: number | null;
  new_solar_benefits: string | null;
  other_changes: string | null;
  loan_product: string | null;
  loan_product_name: string | null;
  borrower_business_name: string | null;
  application_number: string | null;
  loan_term_name: string | null;
  loan_amount: number | null;
  system_property_address: string | null;
  interest_rate: number | null;
  is_system_size_changing: 'Yes' | 'No' | null;
  is_first_year_production_changing: 'Yes' | 'No' | null;
  are_solar_benefits_available: 'Yes' | 'No' | null;
  total_cost_of_solar_plus_components: number | null;
  reason_for_change: string | null;
  reason_for_change_solar: string | null;
  additional_reason_notes: string | null;
  additional_reason_notes_solar: string | null;

  // New values from Change Order 2.0
  new_loan_type: string | null;
  new_loan_term: string | null;
  new_interest_rate: number | null;
  new_special_payment_percentage: number | null;
  new_loan_product: string | null;
  net_funding?: number | null | undefined;
  new_dealer_fee: Fee | null;
  original_project_type: string | null;
  new_project_type: string | null;
  original_asset_types: string | null;
  new_assets_types: string | null;
  other_asset_price: number | null;
  other_asset_description: string | null;
}

// minimal version of ChangeOrder for dashboard
export type DashboardChangeOrder = Pick<
  ChangeOrder,
  | 'id'
  | 'loan_application'
  | 'internal_status'
  | 'new_loan_amount'
  | 'other_changes'
  | 'date_submitted'
  | 'created_date'
  | 'external_status'
  | 'application_number'
  | 'new_loan_product'
  | 'reason_for_change'
  | 'reason_for_change_solar'
  | 'additional_reason_notes_solar'
>;

export interface ChangeOrderWithDocuments extends ChangeOrder {
  documents: Pick<RequiredDocuments, ChangeOrderDocumentTypes>;
}

export interface ChecklistItem {
  id: string;
  is_deleted: boolean;
  name: string | null;
  created_date: string;
  last_modified_date: string;
  system_modstamp: string;
  last_viewed_date: string | null;
  last_referenced_date: string | null;
  action:
    | 'No Action'
    | 'Automated Daily Email alert to Borrower'
    | 'Follow up with the Installer'
    | null;
  borrower_last_notified_date: string | null;
  internal_notes: string | null;
  loan_application: number | null;
  notes_to_borrower: string | null;
  status: 'Verified' | 'Issues Identified' | 'Missing' | null;
  checklist_status: 'New' | 'In Progress' | 'In Review' | 'Done' | null;
  verification_date: string | null;
  template: 'Standard' | 'Optional' | 'UW Checklist' | null;
  document_name: string | null;
  date_completed: string | null;
  sort: number | null;
  start_date: string | null;
  underwriting: string | null;
  days_in_progress: number | null;
  status_color: string | null;
}

export interface Contact {
  id?: any;
  account: Account;
  borrower_business_name?: string;
  created_date?: string;
  email?: string;
  first_name: string;
  installer_role: (typeof INSTALLER_TYPES)[keyof typeof INSTALLER_TYPES] | null;
  is_active?: boolean;
  last_login?: string;
  last_name: string;
  name: string;
  position_title?: string;
  record_type?: 'borrower' | 'installer' | 'consultant';
  status?: 'Invited' | 'Unverified' | 'Active' | 'Inactive';
  feature_flags?: string;
  is_installer?: boolean;
  is_consultant?: boolean;
  is_borrower?: boolean;
  phone?: string;
  mobile_phone?: string;
}

export interface ContactExtended extends Contact {
  created_by_user_contact?: {
    id: string;
    name: string;
  };
}

export type DocumentType = {
  id: string;
  name: string;
  crb_abbrev: string;
};

export interface EmailMessage {
  id?: any;
  created_by_id: string;
  created_date: string;
  last_modified_date: string;
  system_modstamp: string;
  text_body?: string | null;
  html_body?: string | null;
  headers?: string | null;
  subject?: string | null;
  from_name?: string | null;
  from_address?: string | null;
  validated_from_address?:
    | 'eng@sunstonecredit.com'
    | 'team@sunstonecredit.com'
    | 'support@sunstonecredit.com'
    | null;
  to_address?: string | null;
  cc_address?: string | null;
  bcc_address?: string | null;
  incoming?: boolean;
  has_attachment?: boolean;
  status: '0' | '1' | '2' | '3' | '4' | '5';
  message_date?: string | null;
  is_externally_visible?: boolean;
  message_identifier?: string | null;
  thread_identifier?: string | null;
  is_client_managed?: boolean;
  is_tracked?: boolean;
  is_opened?: boolean;
  first_opened_date?: string | null;
  last_opened_date?: string | null;
  is_bounced?: boolean;
  activity?: SFId | null;
  reply_to_email_message?: SFId | null;
  related_to?: SFId | null;
}

export interface Fee
  extends Omit<
    SFObject,
    | 'is_deleted'
    | 'created_date'
    | 'last_modified_date'
    | 'last_activity_date'
    | 'last_viewed_date'
    | 'last_referenced_date'
  > {
  active: boolean;
  amortization_years: string | null;
  deactivation_reason: string | null;
  dealer_fee: number | null;
  end_date: string | null;
  installer: SFId | null;
  interest_rate: number | null;
  loan_term_years: string | null;
  pricing_grouping: '1' | '2' | '3' | '4' | '5' | null;
  product: SFId | null;
  product_family: ProductFamily | null;
  profile: 'Normal' | 'CED' | 'Custom' | 'Legacy' | null;
  start_date: string | null;
  applies_to_assets: string | null;
  exclude_for_assets: string | null;
}

export type File = {
  id: string;
  required_doc_id: string;
  s3_url: string;
  version_id: string;
  file_name: string;
  is_deleted: boolean;
  is_public: boolean;
  created_at: Date;
  created_by: string;
  upload_source: FileUploadSource;
};

export interface FundedProject {
  id?: any;
  active_milestone:
    | 'NTP'
    | 'PO'
    | 'EO'
    | 'SC'
    | 'FC'
    | 'MC'
    | 'RC'
    | 'FullyFunded'
    | null;
  advance_funding_request?: number | null;
  borrower_business_name?: string | null;
  borrower_contact_name?: string | null;
  borrower_email?: string | null;
  borrower_phone?: string | null;
  closed_message: string | null;
  consultant_account: Account;
  created_date: string;
  custom_information?: string | null;
  days_until_expiration_date?: number;
  days_until_pricing_expiration_date?: number;
  decision_date?: string | null;
  detailed_requirements_url: string | null;
  errors?: FundedProjectMessage[] | null;
  external_status?:
    | 'In Progress'
    | 'Pending Change Order'
    | 'Fully Funded'
    | 'Canceled'
    | 'Closed'
    | null;
  final_completion_expiration_date?: string;
  financing_expiration_date?: string;
  original_financing_expiration_date?: string | null;
  original_pricing_expiration_date?: string | null;
  installer_account: Account;
  installer_advance_funding_opt_in: 'Yes' | 'No' | null;
  product: SFId;
  installer_program: InstallerProgram | null;
  last_modified_date: string;
  loan_agreement_signed_date?: string;
  loan_app_interest_rate?: number | null;
  loan_app_loan_amount?: number | null;
  loan_app_number?: string | null;
  loan_app_term_length?: number | null;
  loan_application: Application;
  name?: string | null;
  net_funding?: number | null;
  paid_to_date?: number | null;
  pricing_expiration_date?: string | null;
  type_of_advance_funding?: string | null;
  request_approved: string | null;
  selected_advanced_funding: string | null;
  system_location_address?: string | null;
  system_modstamp: string;
}

// minimal version of FundedProject for dashboard
export interface DashboardFundedProject
  extends Pick<
    FundedProject,
    | 'id'
    | 'active_milestone'
    | 'borrower_business_name'
    | 'borrower_contact_name'
    | 'closed_message'
    | 'consultant_account'
    | 'custom_information'
    | 'days_until_expiration_date'
    | 'days_until_pricing_expiration_date'
    | 'decision_date'
    | 'errors'
    | 'external_status'
    | 'final_completion_expiration_date'
    | 'financing_expiration_date'
    | 'installer_account'
    | 'installer_program'
    | 'loan_app_loan_amount'
    | 'loan_app_number'
    | 'loan_application'
    | 'name'
    | 'net_funding'
    | 'paid_to_date'
    | 'pricing_expiration_date'
    | 'system_location_address'
  > {
  days_since_note_date?: number;
  system_location_address_2?: string;
  system_location_address_3?: string;
}

export interface InstallerDashboardProjectsSummary {
  total_paid_to_date: number;
  net_funding: number;
  projects_in_progress: number;
  completed_projects: number;
}

export interface InstallerDashboardApplicationsSummary {
  total_applications: number;
  total_applicants: number;
  completed_applications: number;
  active_applications: number;
}

export interface ProductModel {
  id: string;
  amortizationYears: number;
  dealerFee: number;
  loanTermYears: number;
  productFamily: ProductFamily;
  rateId: string;
  rate: number;
  rateScenario: string;
  startDate?: string | null;
  type: 'current' | 'future';
}

export interface InstallerProgram {
  amount_of_available_funds: number;
  contractor_type: ContractorType | null;
  decision_date: Date;
  funding_limit: number;
  installer_account: string;
  installer_application: string;
  name: string;
  open_advances: number;
  status:
    | 'Application Submitted'
    | 'Application In-Progress'
    | 'Approved'
    | 'Declined'
    | 'Application Incomplete';
  type: InstallerProgramType;
}

export interface LoanVersion {
  id?: any;
  active_loan_version: boolean;
  loan_version_name: string;
  maturity_payment_amount?: number;
  special_payment_amount?: number;
  special_payment_month?: number;
  borrower_advance_rate?: number;
  pricing_date?: string;
  special_payment_percentage?: number;
}

export interface LogOut {
  refresh: string;
  access: string;
}

export interface MaterialLoanObligation extends SFObject {
  loan_type?: 'Commercial' | 'Personal' | null;
  loan_balance?: number | null;
  loan_maturity_date?: string | null;
  loan_monthly_payment?: number | null;
  loan_original_amount?: number | null;
  loan_collateral_description?: string | null;
  any_payments_30_days_past_due?: 'Yes' | 'No' | null;
  reference_name?: string | null;
  reference_phone_number?: string | null;
  reference_email?: string | null;
  application?: SFId;
  lender_name?: string | null;
  year_of_loan_maturity?: number | null;
  loan_purpose?: string | null;
  outstanding_loan_type?: string | null;
  on_system_property?: string | null;
}

export interface Milestone {
  id?: any;
  approval_1: 'Awaiting Entry' | 'Complete' | null;
  approval_2: 'Awaiting Entry' | 'Complete' | null;
  approval_3: 'Awaiting Entry' | 'Complete' | null;
  completion_date: string | null;
  created_date: string;
  description: string;
  documents?: SunstoneDocument[];
  entered_by_1: SFId | null;
  entered_by_2: SFId | null;
  entered_by_3: SFId | null;
  funded_project: SFId;
  key_1: string | null;
  key_2: string | null;
  key_3: string | null;
  last_modified_date: string;
  milestone_completion: number;
  max_allowed_amount: number;
  name?: string | null;
  estimated_milestone_date?: string | null;
  payment_amount: number;
  payment_percentage: number;
  requested_amount: number | null;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Paid' | 'Waived';
  system_modstamp: string;
  title: string;
  tooltip_1: string | null;
  tooltip_2: string | null;
  tooltip_3: string | null;
  total_of_documents: number;
  total_of_approved_documents: number;
  type: 'NTP' | 'PO' | 'EO' | 'SC' | 'RC' | 'MC' | 'FC';
  type_1: 'Text' | 'Date' | null;
  type_2: 'Text' | 'Date' | null;
  type_3: 'Text' | 'Date' | null;
  value_1: string | null;
  value_2: string | null;
  value_3: string | null;
  warning: string | null;
  equipment_type: string | null;
  document_sort_order: number | null;
  sort_id: string | null;
  submilestones?: Milestone[];
  total_eos: number;
  installer_funding_request_ready?: boolean | null;
  /** Sum of all EO slots' amounts (submitted + pending). Computed client-side in getFundedProjectMilestones. */
  total_eo_pool?: number;
  /** Original EO program % of the loan (e.g. 30). Computed client-side from payment_percentage. */
  eo_program_pct?: number;
}

export interface OutstandingLoan extends SFObject {
  loan_type?: 'Commercial' | 'Personal' | null;
  loan_balance?: number | null;
  loan_maturity_date?: SFDate | null;
  loan_monthly_payment?: number | null;
  loan_original_amount?: number | null;
  loan_collateral_description?: string | null;
  any_payments_30_days_past_due?: YesNo | null;
  reference_name?: string | null;
  reference_phone_number?: number | null;
  reference_email?: string | null;
  application: SFId;
  lender_name?: string | null;
}

export type PaginatedResult<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export interface Payment {
  id?: any;
  account_mask?: string | null;
  amount: number;
  created_date: string;
  funded_project: SFId;
  last_modified_date: string;
  made_on: string;
  milestone: Milestone;
  name?: string | null;
  purpose: string | null;
  receipt_download_url: string | null;
  system_modstamp: string;
  account_type: AccountRecordType;
  destination_bank_account: Account;
  destination_bank_account_name: string | null;
}

export interface Product extends SFObject {
  amortization_years: number;
  apr_percentage: number;
  description: string;
  family: ProductFamily;
  id: string;
  is_active: boolean;
  is_archived?: boolean;
  loan_term_years: number;
  name: string;
}

export interface Rates {
  current: FeeByProductFamily;
  future: {
    date: string;
    rates: FeeByProductFamily;
  }[];
}

export interface RelatedParty {
  id?: any;
  address?: string | null;
  address_line_2?: string | null;
  annual_gross_income?: number | null;
  applicant_same_as_principal?: 'Yes' | 'No' | null;
  application: SFId;
  average_cash_balance?: number | null;
  business_name?: string | null;
  city?: string | null;
  country?: string | null;
  created_date?: any;
  dob?: string | null;
  documents?: {
    PRINCIPAL_ID?: RequiredDocument;
    'Certificate of Trust or Executed Trust Agreement'?: RequiredDocument;
    PGTR1: RequiredDocument;
    PGEX1: RequiredDocument;
    PGTR2: RequiredDocument;
    PGEX2: RequiredDocument;
    PGTR3: RequiredDocument;
    PGEX3: RequiredDocument;
    'PG Tax Returns Last Year': RequiredDocument;
    'PG Tax Extension Last Year': RequiredDocument;
    'PG Tax Returns 2 Years Ago': RequiredDocument;
    'PG Tax Extension 2 Years Ago': RequiredDocument;
    'PG Tax Returns 3 Years Ago': RequiredDocument;
    'PG Tax Extension 3 Years Ago': RequiredDocument;
  }; // these 2 fields are only used by Rosefield for principal validation
  email?: string | null;
  entity_type?: 'Business' | 'Trust' | null;
  first_name?: string | null;
  filed_taxes_1_year_ago?: 'Yes' | 'No' | null;
  filed_taxes_2_years_ago?: 'Yes' | 'No' | null;
  filed_taxes_3_years_ago?: 'Yes' | 'No' | null;
  guarantor_agreement?: boolean | null;
  is_a_non_us_person?: 'Yes' | 'No' | null;
  is_controlling_principal?: boolean;
  is_deleted?: any;
  is_us_citizen: 'Yes' | 'No' | null;
  last_activity_date?: string | null;
  last_modified_date?: any;
  last_name?: string | null;
  last_referenced_date?: string | null;
  last_viewed_date?: string | null;
  liquid_assets?: number | null;
  middle_initial?: string | null;
  monthly_rent?: number | null;
  mortgage_payment?: number | null;
  name?: any;
  name_of_trust?: string | null;
  non_usperson_id_country_of_issuance?: string | null;
  non_us_person_id_number?: string | null;
  other_occupation?: string | null;
  percent_ownership?: number | null;
  property_owner_percent_ownership?: number | null;
  phone_number?: string | null;
  position_title?: string | null;
  principal_is_personal_guarantor?: 'Yes' | 'No' | null;
  principal_type?: 'Individual' | 'Entity' | null;
  relationship?: 'Principal' | 'Guarantor' | 'Offtaker' | 'Landlord' | null;
  rent_or_own?: 'Rent' | 'Own' | null;
  ssn_text?: string | null;
  state_picklist?: USState | null;
  trust_tin?: string | null;
  year_trust_was_established?: number | null;
  zip_code?: string | null;
  __asap_count: number;
  __is_pg_value: boolean;
  __companyTypeValue: string;
  system_property_estimation_method?:
    | 'Self Reported'
    | 'Tax Assessed Value'
    | 'Recent Property Sale'
    | 'Recent Appraisal'
    | null;
  sys_prop_landlord_consent_confirmation?: 'Yes' | 'No' | null;
  sys_prop_owner_related_to_borrower?: 'Yes' | 'No' | null;
  ethnicity: string[];
  sub_ethnicity: string[];
  other_ethnicity: string;
  sex_gender: string;
  race: string[];
  sub_race: string[];
  other_race: Record<string, string>;
}

export type Note = {
  id: string;
  body: string | null;
  created_date: string;
  updated_date: string;
  note_type: 'External' | 'Internal';
};

export type RequiredDocument = {
  id: string;
  approval_date: Date;
  approval_status: DocumentApprovalStatus;
  approved_by_user_username: string | null;
  document_approval_source: DocumentApprovalSource;
  document_type: DocumentType;
  document_type_id: string;
  files: File[];
  is_gallery: boolean;
  is_single_file: boolean;
  month: number;
  notes: Note[];
  parent_id: string;
  requirement_description: string;
  requirement_name: string;
  requires_approval: boolean;
  sub_type: string;
  sunstone_document_id: string | null;
  year: number;
  created_at: string;
  can_view: DocumentUserGroup;
  can_upload: DocumentUserGroup;
  required_for_step: string;
  is_required: boolean;
  analysis?: boolean | null;
};

export type RequiredDocuments = {
  'Solar Loan Proposal'?: RequiredDocument;
  'Utility Bills'?: RequiredDocument[];
  'Incentive Documents'?: RequiredDocument[];
  'New Solar Proposal'?: RequiredDocument;
  plaid_asset_report?: PlaidAssetReport;
  TR: RequiredDocument;
  TR1: RequiredDocument;
  TR2: RequiredDocument;
  'Tax Returns 2 Years Ago': RequiredDocument;
  'Tax Returns 3 Years Ago': RequiredDocument;
  'Tax Returns 4 Years Ago': RequiredDocument;
  'Tax Returns Last Year': RequiredDocument;
  FIN?: RequiredDocument;
  'Interim Financial Statement': RequiredDocument;
  BIZ?: RequiredDocument;
  'Business Organization': RequiredDocument;
  BS: RequiredDocument;
  BS1: RequiredDocument;
  BS2: RequiredDocument;
  'Bank Statements 2 Months Ago': RequiredDocument;
  'Bank Statements 3 Months Ago': RequiredDocument;
  'Bank Statements Last Month': RequiredDocument;
  'Borrower Change Awareness': RequiredDocument;
  OWN: RequiredDocument;
  'Proof of Ownership of the System Property': RequiredDocument;
  PGTR1: RequiredDocument;
  PGEX1: RequiredDocument;
  PGTR2: RequiredDocument;
  PGEX2: RequiredDocument;
  PGTR3: RequiredDocument;
  PGEX3: RequiredDocument;
  'PG Tax Returns Last Year': RequiredDocument;
  'PG Tax Extension Last Year': RequiredDocument;
  'PG Tax Returns 2 Years Ago': RequiredDocument;
  'PG Tax Extension 2 Years Ago': RequiredDocument;
  'PG Tax Returns 3 Years Ago': RequiredDocument;
  'PG Tax Extension 3 Years Ago': RequiredDocument;
  PRINCIPAL_ID?: RequiredDocument;
  'Principal ID'?: RequiredDocument;
  'Articles of Organization/Incorporation'?: RequiredDocument;
  'Non-Profit Board Resolution'?: RequiredDocument;
  'Non-Profit Bylaws'?: RequiredDocument;
  'Property Company Rent Roll'?: RequiredDocument;
  'Lease of the property where the system will be installed'?: RequiredDocument;
  'Lease of the System Property'?: RequiredDocument;
  'Proof of Ownership of the property where the system will be installed'?: RequiredDocument;
  'Offtaker PPA or Lease Agreement'?: RequiredDocument[];
  'Proof of Offtaker Creditworthiness'?: RequiredDocument[];
  'Offtaker Lease or Proof of Ownership'?: RequiredDocument;
  'Offtaker Utility Bill'?: RequiredDocument[];
  'PPA/Lease Financial Projections'?: RequiredDocument;
  'Subscriber Management Company Contract'?: RequiredDocument;
  'Community Solar Financial Projections'?: RequiredDocument[];
  'Approval for Community Solar Delivery'?: RequiredDocument;
  'Financial Statements YTD'?: RequiredDocument;
  'Financial Statements Last Year'?: RequiredDocument;
  'Financial Statements One Year Ago'?: RequiredDocument;
  'Financial Statements Two Years Ago'?: RequiredDocument;
  'IRS Form SS-4'?: RequiredDocument;
  'Tax Returns Extension (Form 7004)'?: RequiredDocument;
  'Debt Schedule'?: RequiredDocument;
  // Change order documents
  'Change Order Contract'?: RequiredDocument;
  // Religious Organization documents
  'Religious Organization Attendance'?: RequiredDocument;
  'Religious Organization Donations'?: RequiredDocument;
  // HOA Documents
  'Member Assessment Policy'?: RequiredDocument;
  'Operating and Reserve Budgets'?: RequiredDocument;
  'Current Occupant Ledger'?: RequiredDocument;
  'Delinquency Report'?: RequiredDocument;
  'Reserve Study'?: RequiredDocument;
  'Meeting Minutes'?: RequiredDocument;
  'HOA Bylaws'?: RequiredDocument;
  'Property Company Mortgage Statement'?: RequiredDocument;
  'Mortgage Statement'?: RequiredDocument;
  'Additional Document'?: RequiredDocument[];
  FinancialStatementQ1: RequiredDocument;
  FinancialStatementQ2: RequiredDocument;
  FinancialStatementQ3: RequiredDocument;
  FinancialStatementLastYearQ3: RequiredDocument;
  FinancialStatementFullYear: RequiredDocument;
  'Financial Statement Q1': RequiredDocument;
  'Financial Statement Q2': RequiredDocument;
  'Financial Statement Q3': RequiredDocument;
  'Financial Statement Last Year Q3': RequiredDocument;
  'Financial Statement Full Year': RequiredDocument;
  'Financial Statement Two Years Ago Full Year': RequiredDocument;
  'Financial Statement Three Years Ago Full Year': RequiredDocument;
  'Financial Statement Four Years Ago Full Year': RequiredDocument;

  // New Document Types
  'Business Secretary of State'?: RequiredDocument;
  'Equipment Order Draw Form Signed'?: RequiredDocument;
  'Equipment Order Draw Form'?: RequiredDocument;
  'GA Equipment Order Form'?: RequiredDocument;
  'GA Loan Agreement'?: RequiredDocument;
  'GA Mechanical Completion Form'?: RequiredDocument;
  'GA Racking Completion Form'?: RequiredDocument;
  'HOA Current Occupant Ledger'?: RequiredDocument;
  'Loan Agreement Signed'?: RequiredDocument;
  'Mechanical Completion Draw Form (signed)'?: RequiredDocument;
  'Mechanical Completion Draw Form'?: RequiredDocument;
  'Racking Completion Draw Form (unsigned)'?: RequiredDocument;
  'Racking Completion Draw Form'?: RequiredDocument;
  'UCC-1 Financing Statement'?: RequiredDocument;
  'UCC-3'?: RequiredDocument;
  'UCC-1 Fixture Notice'?: RequiredDocument;
  'PV Watts'?: RequiredDocument;
  'Borrower Profile'?: RequiredDocument;
  'Property Appraisal'?: RequiredDocument;
  'Tax Return for Leasing PropCo'?: RequiredDocument;
};

export type RequiredDocumentsWithRelatedPartyDocs = RequiredDocuments & {
  related_parties?: Record<string, RequiredDocuments>;
};

interface SFObject {
  id: SFId;
  name: string;
  is_deleted: boolean;
  created_date: SFDate;
  last_modified_date: SFDate;
  last_activity_date: SFDate | null;
  last_viewed_date?: SFDate | null;
  last_referenced_date?: SFDate | null;
}

export interface SunstoneDocument {
  id?: any;
  approval_status?:
    | 'Sent via Docusign'
    | 'Awaiting Upload'
    | 'Signature Needed'
    | 'Waiting Review'
    | 'Approved'
    | 'Issues Identified'
    | 'Pending Call'
    | 'Waived'
    | 'Missing'
    | null;
  created_date: string;
  description?: string | null;
  display_name?: string | null;
  external_notes?: string | null;
  document_source?: 'DocuSign' | 'Direct-Upload' | null;
  document_type:
    | 'Additional Document'
    | 'Tax Returns Last Year'
    | 'Tax Returns 2 Years Ago'
    | 'Tax Returns 3 Years Ago'
    | 'Tax Returns 4 Years Ago'
    | 'Interim Financial Statement'
    | 'Bank Statement Last Month'
    | 'Bank Statement 1 Month Ago'
    | 'Bank Statement 2 Months Ago'
    | 'Ownership Document'
    | 'Asset Report'
    | 'Compressed File'
    | 'Installer Milestone-Funding Document'
    | 'Equipment Invoice';
  e_signer_email?: string | null;
  e_signer_name?: string | null;
  file_created_date?: string | null;
  file_name?: string;
  file_size?: number;
  frontend_upload_type: 'Single File' | 'Gallery' | null;
  has_no_major_subtractors_or_suppliers: boolean;
  last_modified_date: string;
  last_submission_date: string | null;
  loan_application: SFId;
  milestone?: SFId | null;
  name?: string | null;
  original_file_name?: string;
  owner_account?: SFId | null;
  possibly_unrequired: 'Yes' | 'No' | null;
  related_entity_name?: string | null;
  required_for_step: string | null;
  requires_approval: 'Yes' | 'No' | null;
  sort_id?: number | null;
  system_modstamp: string;
  tooltip_text?: string | null;
  unrequired_text: string | null;
  uploaded_by?: SFId;
  url: string | null;
  file_id?: string | null;
}

export interface Task {
  id?: any;
  who_count?: number | null;
  what_count?: number | null;
  subject?: 'CALL' | 'SEND LETTER' | 'SEND QUOTE' | 'OTHER' | null;
  activity_date?: string | null;
  status?: 'OPEN' | 'COMPLETED';
  priority?: 'HIGH' | 'NORMAL';
  is_high_priority?: boolean;
  description?: string | null;
  is_closed?: boolean;
  created_date: string;
  last_modified_date: string;
  system_modstamp: string;
  is_archived?: boolean;
  call_duration_in_seconds?: number | null;
  call_type?: 'Internal' | 'Inbound' | 'Outbound' | null;
  call_disposition?: string | null;
  call_object?: string | null;
  reminder_date_time?: string | null;
  is_reminder_set?: boolean;
  is_recurrence?: boolean;
  recurrence_start_date_only?: string | null;
  recurrence_end_date_only?: string | null;
  recurrence_time_zone_sid_key?: string | null;
  recurrence_type?:
    | 'RecursDaily'
    | 'RecursEveryWeekday'
    | 'RecursMonthly'
    | 'RecursMonthlyNth'
    | 'RecursWeekly'
    | 'RecursYearly'
    | 'RecursYearlyNth'
    | null;
  recurrence_interval?: number | null;
  recurrence_day_of_week_mask?: number | null;
  recurrence_day_of_month?: number | null;
  recurrence_instance?: 'First' | 'Second' | 'Third' | 'Fourth' | 'Last' | null;
  recurrence_month_of_year?:
    | 'January'
    | 'February'
    | 'March'
    | 'April'
    | 'May'
    | 'June'
    | 'July'
    | 'August'
    | 'September'
    | 'October'
    | 'November'
    | 'December'
    | null;
  recurrence_regenerated_type?:
    | 'RecurrenceRegenerateAfterDueDate'
    | 'RecurrenceRegenerateAfterToday'
    | 'RecurrenceRegenerated'
    | null;
  task_subtype?: 'Task' | 'Email' | 'ListEmail' | 'Cadence' | 'Call' | null;
  completed_date_time?: string | null;
  borrower_communication?:
    | 'Application Created'
    | 'Application in Progress'
    | 'Waiting for Additional Information from Borrower'
    | 'Application Completed and Submitted for Review'
    | 'Application Review in Progress'
    | 'Application Review Complete'
    | 'Application Secondary Review Pending'
    | 'Loan Agreement Package Sent to Borrower'
    | 'Loan Agreement Signed by Borrower'
    | 'Loan Agreement Package Finalized'
    | 'Loan Funded'
    | 'Loan Declined'
    | 'Loan Cancelled by Installer'
    | 'Loan Cancelled Due to Inactivity'
    | string
    | null;
  who?: SFId | null;
  what?: SFId | null;
  account?: SFId | null;
  recurrence_activity?: SFId | null;
}

export interface TokenRefresh {
  refresh: string;
}

export interface UpdatePassword {
  current_password: string;
  new_password: string;
  confirm_password: string;
  refresh_token: string;
}

export interface User extends SFObject {
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone?: string;
}

export interface WebPortalResource extends SFObject {
  resource_name: string;
  accessible_by: 'Installers' | 'Borrowers' | 'All' | 'Custom' | null;
  custom_access_account: string | null;
  description: string;
  tooltip: string | null;
  resource_type: 'Rate sheet' | 'Installer Document' | 'Borrower Document';
  start_date: string | null;
  end_date: string | null;
  download_link: string | null;
  preview_link: string | null;
  content_delivery_id: string | null;
  content_document_id: string | null;
}

export type TrainingVideoLinks = {
  id: string;
  title: string;
  created_at: string;
  description: string;
  url: string;
};

export type PricingSummary = {
  monthly_payment: number;
  yearly_payments: number[];
  special_payment: number;
  year_1_payment: number;
  maturity_payment: number;
};

export enum AccountRecordType {
  INSTALLER = 'Installer',
  CONSULTANT = 'Consultant',
  BORROWER = 'Borrower',
  PARTNER = 'Partner',
  VENDORS_DISTRIBUTORS = 'Vendors/Distributors',
}
